<template>
  <div class="strategy-customization" ref="containerRef">
    <div class="d-flex flex-column">
      <!-- Strategy Accordion -->
      <div class="accordion custom-accordion" id="strategyAccordion">
        <!-- Forward Sales Accordion Item -->
        <div
          v-if="company.feature_flags?.forward_sales_enabled"
          class="accordion-item"
        >
          <h2 class="accordion-header">
            <button
              class="accordion-button bg-light"
              :class="{ collapsed: activeStrategyTab !== 'forward' }"
              type="button"
              @click="setStrategyTab('forward')"
              :aria-expanded="activeStrategyTab === 'forward'"
            >
              <span class="strategy-title">
                <font-awesome-icon icon="calendar-alt" class="me-2" />
                <span>{{
                  $t("tradingCenter.customization.forwardSales")
                }}</span>
              </span>
            </button>
          </h2>
          <div
            class="accordion-collapse collapse"
            :class="{ show: activeStrategyTab === 'forward' }"
          >
            <div class="accordion-body p-0">
              <div class="bg-light rounded-bottom">
                <div class="px-3 py-3">
                  <ForwardSalesTabComponent :company />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- PPA Strategy Accordion Item -->
        <div v-if="showPPAStrategy" class="accordion-item">
          <h2 class="accordion-header">
            <button
              class="accordion-button bg-light"
              :class="{ collapsed: activeStrategyTab !== 'ppa' }"
              type="button"
              @click="setStrategyTab('ppa')"
              :aria-expanded="activeStrategyTab === 'ppa'"
            >
              <span class="strategy-title">
                <font-awesome-icon icon="file-contract" class="me-2" />
                <span>{{ $t("tradingCenter.customization.ppaReserve") }}</span>
              </span>
            </button>
          </h2>
          <div
            class="accordion-collapse collapse"
            :class="{ show: activeStrategyTab === 'ppa' }"
          >
            <div class="accordion-body p-0">
              <div class="bg-light rounded-bottom">
                <div class="px-3 py-3">
                  <PPAStrategySettings
                    v-if="companyData && isInitialized"
                    :company="companyData"
                    :registry-devices="registryDevices"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Hold Strategy Accordion Item -->
        <div v-if="showHoldStrategy" class="accordion-item">
          <h2 class="accordion-header">
            <button
              class="accordion-button bg-light"
              :class="{ collapsed: activeStrategyTab !== 'hold' }"
              type="button"
              @click="setStrategyTab('hold')"
              :aria-expanded="activeStrategyTab === 'hold'"
            >
              <span class="strategy-title">
                <font-awesome-icon icon="hand-paper" class="me-2" />
                <span>{{
                  $t("tradingCenter.customization.holdPortfolio")
                }}</span>
              </span>
            </button>
          </h2>
          <div
            class="accordion-collapse collapse"
            :class="{ show: activeStrategyTab === 'hold' }"
          >
            <div class="accordion-body p-0">
              <div class="bg-light rounded-bottom">
                <div class="px-3 py-3">
                  <HoldStrategySettings
                    v-if="companyData && isInitialized"
                    :company="companyData"
                    :registry-devices="registryDevices"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Spot Sales Accordion Item -->
        <div
          class="accordion-item"
          :class="{ 'active-strategy': activeStrategyTab === 'spot' }"
        >
          <h2 class="accordion-header">
            <button
              class="accordion-button bg-light"
              :class="{
                collapsed: activeStrategyTab !== 'spot',
              }"
              type="button"
              @click="setStrategyTab('spot')"
              :aria-expanded="activeStrategyTab === 'spot'"
            >
              <span class="strategy-title">
                <font-awesome-icon icon="bolt" class="me-2" />
                <span>{{ $t("tradingCenter.customization.spotSales") }}</span>
              </span>
            </button>
          </h2>
          <div
            class="accordion-collapse collapse"
            :class="{ show: activeStrategyTab === 'spot' }"
          >
            <div class="accordion-body p-0">
              <div class="bg-light rounded-bottom">
                <div class="px-3 py-3">
                  <SpotStrategyPicker
                    v-if="companyData"
                    :initialStrategy="initialStrategy"
                    @update:strategy="handleStrategyChanged"
                  />
                  <button
                    type="button"
                    class="btn btn-primary w-100 mt-3"
                    :disabled="!isSpotStrategyValid || isLoading"
                    @click="handleSaveStrategy"
                  >
                    {{ $t("setPriceTerms.saveChanges") }}
                    <span
                      v-if="isLoading"
                      class="spinner-border spinner-border-sm ms-1"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAccount } from "@/composables/account/useAccount";
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { useCompanySpotStrategy } from "@/composables/useCompanySpotStrategy";
import { useFeatureFlags } from "@/composables/useFeatureFlags";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { Tab, useTabs } from "@/composables/useTabs";
import SpotStrategyPicker from "@/dashboard/common/SpotStrategyPicker.vue";
import { useProductionStrategyStore } from "@/dashboard/production_strategy/useProductionStrategyStore";
import { Company, RegistryDevice } from "@/types";
import { onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import ForwardSalesTabComponent from "./forwards/ForwardSalesTabComponent.vue";
import PPAStrategySettings from "./PPA/PPAStrategySettings.vue";
import HoldStrategySettings from "./spot/HoldStrategySettings.vue";
const props = defineProps<{
  company: Company;
  registryDevices: RegistryDevice[];
}>();

const { t } = useI18n();
const route = useRoute();
const { hasFlag } = useFeatureFlags(props.company);

// Feature flag checks
const showPPAStrategy = hasFlag("ppa_allocation");
const showHoldStrategy = hasFlag("hold_allocation");

// Strategy tabs configuration with direct English labels
const strategyTabs: Tab[] = [
  {
    id: "spot",
    name: "Spot Sales",
  },
  {
    id: "forward",
    name: "Forward Sales",
  },
  {
    id: "ppa",
    name: "PPA Strategy",
  },
  {
    id: "hold",
    name: "Hold Strategy",
  },
];

// Use the tabs composable with a function for dynamic defaultTabId
const { activeTab: activeStrategyTab, setTab } = useTabs("strategy", {
  defaultTabId: () => {
    const queryTab = route.query["strategy-active-tab"] as string | undefined;

    // First check if we have a valid tab ID in the URL query
    if (
      queryTab === "forward" ||
      queryTab === "spot" ||
      queryTab === "ppa" ||
      queryTab === "hold"
    ) {
      // If forward tab is requested, verify that the company sells forward
      if (
        queryTab === "forward" &&
        !props.company.feature_flags?.forward_sales_enabled
      ) {
        return "spot";
      }

      // If PPA tab is requested, verify that feature flag allows it
      if (queryTab === "ppa" && !showPPAStrategy.value) {
        return "spot";
      }

      // If Hold tab is requested, verify that feature flag allows it
      if (queryTab === "hold" && !showHoldStrategy.value) {
        return "spot";
      }

      return queryTab;
    }

    // Default fallback
    return "spot";
  },
  tabs: strategyTabs,
  allowNoActiveTab: true,
});

const containerRef = useTemplateRef("containerRef");

// Scroll the container into view when a tab is clicked
const setStrategyTab: typeof setTab = (tabId) => {
  if (!containerRef.value) return;

  // If the clicked tab is already active, close it
  if (activeStrategyTab.value === tabId) {
    setTab(null);
    return;
  }

  const card = containerRef.value.closest(".card");
  card?.scrollIntoView({ behavior: "smooth" });

  setTab(tabId);
};

const { accountData } = useAccount(() => onAccountLoaded());
const { selectedCompanyOrFirst: companyData } = useCompanyFinder(accountData);
const initialStrategy = useCompanySpotStrategy();

const isLoading = ref(false);
const isInitialized = ref(false);

// Spot strategy state
const currentSpotStrategy = ref<{
  type: "market" | "minimum";
  minimumPrice?: number;
  durationMonths?: number;
  isValid: boolean;
}>({
  type: "market",
  isValid: true,
});

const isSpotStrategyValid = ref(true);

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const onAccountLoaded = () => {
  isInitialized.value = true;

  // Check if current tab is still valid and switch if needed
  if (
    activeStrategyTab.value === "forward" &&
    !props.company.feature_flags?.forward_sales_enabled
  ) {
    setStrategyTab("spot");
    return;
  }

  if (activeStrategyTab.value === "ppa" && !showPPAStrategy.value) {
    setStrategyTab("spot");
    return;
  }

  if (activeStrategyTab.value === "hold" && !showHoldStrategy.value) {
    setStrategyTab("spot");
    return;
  }
};

const handleStrategyChanged = (strategy: {
  type: "market" | "minimum";
  minimumPrice?: number;
  durationMonths?: number;
  isValid: boolean;
}) => {
  currentSpotStrategy.value = {
    type: strategy.type,
    minimumPrice: strategy.minimumPrice,
    durationMonths: strategy.durationMonths,
    isValid: strategy.isValid,
  };
  isSpotStrategyValid.value = strategy.isValid;
};

const productionStore = useProductionStrategyStore();

const handleSaveStrategy = async () => {
  if (!isSpotStrategyValid.value || isLoading.value) return;

  isLoading.value = true;
  try {
    if (!companyData.value?.id) throw new Error("Missing company id");

    // Save spot strategy directly through the store
    const success = await productionStore.saveSpotStrategy(
      companyData.value.id,
      {
        type: currentSpotStrategy.value.type,
        minimumPrice: currentSpotStrategy.value.minimumPrice,
        durationMonths: currentSpotStrategy.value.durationMonths,
      }
    );

    if (success) {
      globalSuccessHandler(
        t("dashboard.companyData.update_min_price_success"),
        t("dashboard.companyData.new_min_price_effective_immediately")
      );
    }
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  try {
    if (companyData.value?.id) {
      isLoading.value = true;
      await productionStore.refreshMultiCompanyData([companyData.value]);
    }
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped lang="scss">
.strategy-customization {
  max-width: 800px;
}

.custom-accordion {
  --phoenix-accordion-border-width: 0;
  --phoenix-accordion-btn-padding-y: 1rem;
  --phoenix-accordion-btn-padding-x: 1.25rem;
  --phoenix-accordion-active-color: var(--phoenix-gray-900);
  --phoenix-accordion-active-bg: var(--phoenix-white);
}

.accordion-item {
  padding: 0;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: var(--phoenix-light-bg-subtle);
  border: none;

  &:not(:last-child) {
    margin-bottom: 1rem;
  }
}

.active-strategy {
  background-color: var(--phoenix-light-bg-subtle);
}

.accordion-button {
  padding: var(--phoenix-accordion-btn-padding-y)
    var(--phoenix-accordion-btn-padding-x);
  display: flex;
  align-items: center;
  background-color: var(--phoenix-light-bg-subtle);
  border: none;
}

.accordion-button:not(.collapsed) {
  color: var(--phoenix-gray-900);
  background-color: var(--phoenix-light-bg-subtle);
  font-weight: 500;
  border: none;
}

.accordion-button::after {
  width: 1.25rem;
  height: 1.25rem;
  background-size: 1.25rem;
  opacity: 0.5;
}

.accordion-button:not(.collapsed)::after {
  opacity: 0.75;
}

.accordion-button:focus {
  box-shadow: none;
  z-index: 3;
}

.accordion-button:hover {
  background-color: var(--phoenix-light-bg-subtle);
}

.accordion-button:not(.collapsed):hover {
  background-color: var(--phoenix-light-bg-subtle);
}

.strategy-title {
  font-weight: 700;
  display: flex;
  align-items: center;
  color: var(--phoenix-gray-900);
}

.strategy-title .svg-inline--fa {
  color: var(--phoenix-gray-900);
}

.accordion-body {
  background-color: var(--phoenix-white);
  border-top: 1px solid var(--phoenix-gray-200);
}

.accordion-collapse {
  background-color: var(--phoenix-white);
  transition: none !important;
}

@media (max-width: 768px) {
  .accordion-item {
    margin-bottom: 0.75rem;
  }

  .custom-accordion {
    --phoenix-accordion-btn-padding-y: 1rem;
    --phoenix-accordion-btn-padding-x: 1.25rem;
  }
}
</style>
